/**
 * Filter Orderer
 * Handles custom ordering of filter values based on product type
 */
class FilterOrderer {
  constructor() {
    this.orderConfigs = {
      footwear: RICKOWENS.filters.footwear_filter_order,
      clothing: RICKOWENS.filters.clothing_filter_order,
      accessories: RICKOWENS.filters.accessories_filter_order,
    };
  }

  /**
   * Reorders filter values according to a predefined order configuration
   * @param {Array} filterValues - Array of filter value objects
   * @param {String} order - Pipe-separated order string (e.g., "value1,value2|value3,value4")
   * @param {String} key - Property name to match against (e.g., 'value')
   * @returns {Array} Reordered array with separators inserted
   */
  reorderFilters(filterValues, order, key) {
    const SECTIONS_SEPARATOR = '|';
    const VALUES_SEPARATOR = ',';
    const reorderedValues = [];
    const processedValues = new Set();

    const sections = order.split(SECTIONS_SEPARATOR);

    sections.forEach((section) => {
      const sectionValues = section.split(VALUES_SEPARATOR);
      let hasMatches = false;

      sectionValues.forEach((orderedValue) => {
        const matchingValue = filterValues.find((value) => value[key] === orderedValue);
        if (matchingValue) {
          reorderedValues.push(matchingValue);
          processedValues.add(matchingValue[key]);
          hasMatches = true;
        }
      });

      // Add separator between sections if this section had matches
      if (hasMatches) {
        reorderedValues.push({ param_name: 'separator' });
      }
    });

    // Add any remaining values that weren't in the order config
    filterValues.forEach((value) => {
      if (!processedValues.has(value[key])) {
        reorderedValues.push(value);
      }
    });

    return reorderedValues;
  }

  /**
   * Gets the appropriate order configuration based on filter name
   * @param {String} filterName - The filter parameter name
   * @returns {String|null} Order configuration string or null
   */
  getOrderStrategy(filterName) {
    if (filterName.includes('shoe_size')) {
      return this.orderConfigs.footwear;
    }
    if (filterName.includes('accessories_size')) {
      return this.orderConfigs.accessories;
    }
    return this.orderConfigs.clothing;
  }
}

/**
 * RoFacetsFilters Custom Element
 * Handles rendering and interaction for facet filters (product types and categories)
 */
if (!customElements.get('ro-facets-filters')) {
  customElements.define(
    'ro-facets-filters',
    class RoFacetsFilters extends HTMLElement {
      // Constants
      static SELECTORS = {
        filterType: '.filter-type',
        filterTypeInputs: '.filter-type input',
        categoryLinks: 'a:not(.disabled)',
        allLinks: 'a',
        filterList: 'ul',
        jsonData: '[type="application/json"]',
      };

      static CLASSES = {
        active: 'active',
        hidden: 'tw-hidden',
        disabled: 'disabled',
      };

      static DATA_ATTRIBUTES = {
        type: 'type',
        values: 'values',
        name: 'name',
        child: 'child',
        value: 'value',
        macroParent: 'macroParent',
      };

      /**
       * Initialize the component
       */
      constructor() {
        super();
        this.initializeProperties();
        this.initializeComponent();
        this.handleSubscriptionFacetsUpdate = this.handleSubscriptionFacetsUpdate.bind(this);
      }

      /**
       * Initialize component properties
       */
      initializeProperties() {
        const formElement = this.closest('ro-facet-filters-form');
        this.isMobile = formElement?.dataset.mode === 'mobile';
        this.idPrefix = this.isMobile ? 'Mobile-Filter' : 'Filter';
        this.facetForm = document.querySelector('#FacetsWrapperDesktop');
        this.urlParams = new URLSearchParams(window.location.search);
        this.isMacroCategories = this.dataset[RoFacetsFilters.DATA_ATTRIBUTES.type] === 'macro-categories';
      }

      /**
       * Initialize component based on type
       */
      initializeComponent() {
        if (this.isMacroCategories) {
          this.renderAllFilters();
        } else {
          this.filterOrderer = new FilterOrderer();
          this.renderFilter();
        }
      }

      /**
       * Lifecycle: Component connected to DOM
       */
      connectedCallback() {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.facetsUpdate, this.handleSubscriptionFacetsUpdate);
      }

      /**
       * Lifecycle: Component disconnected from DOM
       */
      disconnectedCallback() {
        if (this.unsubscribe) {
          this.unsubscribe();
        }
      }

      /**
       * Handle facets update event from pub/sub
       */
      handleSubscriptionFacetsUpdate() {
        this.renderAllFilters();
      }

      /**
       * Attach event listeners to category filter links
       */
      attachEventListeners() {
        const links = this.querySelectorAll(RoFacetsFilters.SELECTORS.categoryLinks);
        links.forEach((link) => {
          link.addEventListener('click', (event) => {
            event.preventDefault();
            this.updateFacets(event.target);
          });
        });
      }

 
      /**
       * Extract filter data from a link element
       * @param {HTMLElement} element - The link element
       * @returns {Object} Filter data object
       */
      extractFilterData(element) {
        return {
          filterChildParam: element.dataset[RoFacetsFilters.DATA_ATTRIBUTES.child],
          filterParam: element.dataset[RoFacetsFilters.DATA_ATTRIBUTES.name],
          filterValue: element.dataset[RoFacetsFilters.DATA_ATTRIBUTES.value],
        };
      }


      /**
       * Get the filter type container element
       * @returns {HTMLElement|null} Container element or null
       */
      getFilterTypeContainer() {
        return this.facetForm?.querySelector(RoFacetsFilters.SELECTORS.filterType);
      }

     

      /**
       * Render all filters (macro categories)
       */
      renderAllFilters() {
        this.renderMacroCategoryFilters();
        this.attachEventListeners();
      }

      /**
       * Update facets when a category link is clicked
       * @param {HTMLElement} clickedElement - The clicked link element
       */
      updateFacets(clickedElement) {
        const accumulatedValue = this.getAccumulatedFilterValue(clickedElement);
        this.updateLinkStates(accumulatedValue);
      }

      /**
       * Get accumulated filter value from URL parameters
       * @param {HTMLElement} element - The clicked element
       * @returns {String} Accumulated filter value
       */
      getAccumulatedFilterValue(element) {
        const filterChild = element.dataset[RoFacetsFilters.DATA_ATTRIBUTES.child];
        const filterName = element.dataset[RoFacetsFilters.DATA_ATTRIBUTES.name];
        let accumulatedValue = element.dataset[RoFacetsFilters.DATA_ATTRIBUTES.value];

        if (this.urlParams.size > 0) {
          for (const [key, value] of this.urlParams.entries()) {
            if (key === filterChild || key === filterName) {
              accumulatedValue += value;
            }
          }
        }

        return accumulatedValue;
      }

      /**
       * Update active states of all category links
       * @param {String} currentValue - Current filter value to match
       */
      updateLinkStates(currentValue) {
        this.querySelectorAll(RoFacetsFilters.SELECTORS.allLinks).forEach((link) => {
          const linkValue = link.dataset[RoFacetsFilters.DATA_ATTRIBUTES.value];
          
          if (currentValue.indexOf(linkValue) > -1) {
            link.classList.add(RoFacetsFilters.CLASSES.active);
          } else {
            link.classList.remove(RoFacetsFilters.CLASSES.active);
          }
        });
      }

    

      /**
       * Render macro category filters (product types)
       */
      renderMacroCategoryFilters() {
        const filterData = this.getFilterData();
        
        if (!filterData || !this.isMacroCategories) {
          return;
        }

        // Reset cache before rendering so active category is recalculated
        this.resetActiveCategoryCache();

        const listElement = this.querySelector(RoFacetsFilters.SELECTORS.filterList);
        if (!listElement) return;

        listElement.innerHTML = '';

        filterData.values.forEach((value) => {
          const facetElement = this.createFacet(value, true);
          listElement.appendChild(facetElement);
        });
      }

      /**
       * Create a facet element from filter data
       * @param {Object} filter - Filter data object
       * @param {Boolean} isLink - Whether to render as a link (for product types)
       * @param {Boolean} isSize - Whether this is a size filter
       * @returns {HTMLElement} Created list item element
       */
      createFacet(filter, isLink = false, isSize = false) {
        if (filter.param_name === 'separator') {
          return this.createSeparatorElement();
        }

        const facetState = this.getFacetState(filter, isLink);
        const htmlTemplate = this.buildFacetHTML(filter, facetState, isLink, isSize);
        
        return this.parseHTML(htmlTemplate);
      }

      /**
       * Create a separator element
       * @returns {HTMLElement} Separator list item
       */
      createSeparatorElement() {
        const html = `<li class="tw-h-1 tw-bg-lightgrey tw-my-10 tw-w-full lg:tw-w-10/12 lg:tw-mt-[0.7rem] lg:tw-mb-[0.9rem] last-of-type:tw-hidden">
          <span class="tw-invisible">divider</span>
        </li>`;
        return this.parseHTML(html);
      }

      /**
       * Get facet state (disabled, active, checked, etc.)
       * @param {Object} filter - Filter data
       * @param {Boolean} isLink - Whether this is a link
       * @returns {Object} State object with properties
       */
      getFacetState(filter, isLink) {
        const isDisabled = filter.count == 0;
        const isActive = filter.active === 'true';
        const isDisabledForced = isLink ? this.checkForcedDisabledState(filter) : '';

        return {
          isDisabled,
          isActive,
          isDisabledForced,
          disabledAttr: isDisabled ? 'disabled' : '',
          checkedAttr: isActive ? 'checked' : '',
          activeClass: isActive ? RoFacetsFilters.CLASSES.active : '',
          disabledClass: isDisabled ? 'facet-checkbox--disabled disabled' : '',
          hiddenClass: isLink || false ? '' : 'disabled',
        };
      }

      /**
       * Check if a filter should be forced to disabled state
       * @param {Object} filter - Filter data
       * @returns {String} 'disabled' or empty string
       */
      checkForcedDisabledState(filter) {
        const forms = document.querySelectorAll('ro-facet-filters-form');
        
        for (const form of forms) {
          const allInputs = form.querySelectorAll('input[data-value]:disabled');
          const matchingInput = Array.from(allInputs).find(
            (input) => input.dataset[RoFacetsFilters.DATA_ATTRIBUTES.value] === filter.value
          );
          
          if (matchingInput) {
            return 'disabled';
          }
        }
        
        return '';
      }

      /**
       * Build HTML template for facet element
       * @param {Object} filter - Filter data
       * @param {Object} state - Facet state object
       * @param {Boolean} isLink - Whether to render as link
       * @param {Boolean} isSize - Whether this is a size filter
       * @returns {String} HTML template string
       */
      buildFacetHTML(filter, state, isLink, isSize) {
        if (this.isMobile) {
          return this.buildMobileFacetHTML(filter, state);
        }
        
        if (isLink) {
          return this.buildLinkFacetHTML(filter, state);
        }
        
        return this.buildCheckboxFacetHTML(filter, state);
      }

      /**
       * Build mobile facet HTML
       * @param {Object} filter - Filter data
       * @param {Object} state - Facet state
       * @returns {String} HTML template
       */
      buildMobileFacetHTML(filter, state) {
        const id = `${this.idPrefix}-${filter.param_name}-${filter.value.toLowerCase()}`;
        
        return `
          <li class="list-menu__item facets__item tw-form-field tw-flex tw-gap-gutter facets__${filter.param_name} ${state.hiddenClass}">
            <input
              class="tw-checkbox"
              name="${filter.param_name}"
              type="checkbox"
              ${state.checkedAttr}
              ${state.disabledAttr}
              id="${id}"
              value="${filter.value}"
            >
            <label
              class="tw-form-label ${state.disabledAttr} ${state.activeClass}"
              for="${id}"
            >
              <span class="facet-checkbox__text tw-pointer-events-none" aria-hidden="true">
                <span class="facet-checkbox__text-label">${filter.value}</span>
              </span>
              <span class="tw-visually-hidden">${filter.label}</span>
            </label>
          </li>
        `;
      }

      /**
       * Get the active category value (only one category can be active at a time)
       * @returns {String|null} The active category value or null
       */
      getActiveCategoryValue() {
        if (this._activeCategoryValue !== undefined) {
          return this._activeCategoryValue;
        }

        const facetForm = document.querySelector('#FacetFiltersForm');
        const checkedInputs = facetForm ? facetForm.querySelectorAll('input[type="checkbox"]:checked') : [];
        
        // Count children for each category
        const categoryCounts = {};
        
        checkedInputs.forEach(input => {
          const macroParent = input.dataset.macroParent;
          if (macroParent) {
            const parentValue = macroParent.toLowerCase();
            categoryCounts[parentValue] = (categoryCounts[parentValue] || 0) + 1;
          }
        });

        // Find the category with the most children (or first one if there's a tie)
        let activeCategory = null;
        let maxCount = 0;
        
        for (const [category, count] of Object.entries(categoryCounts)) {
          if (count > maxCount) {
            maxCount = count;
            activeCategory = category;
          }
        }

        this._activeCategoryValue = activeCategory;
        return activeCategory;
      }

      /**
       * Reset the cached active category value (call when filters change)
       */
      resetActiveCategoryCache() {
        this._activeCategoryValue = undefined;
      }

      /**
       * Build link facet HTML (for product types)
       * @param {Object} filter - Filter data
       * @param {Object} state - Facet state
       * @returns {String} HTML template
       */
      buildLinkFacetHTML(filter, state) {
        // Get the single active category value
        const activeCategoryValue = this.getActiveCategoryValue();
        const filterValue = filter.value.toLowerCase();
        
        // Only apply active class if this filter matches the active category
        const activeClass = (activeCategoryValue && activeCategoryValue === filterValue) ? 'active' : '';

        const id = `${this.idPrefix}-${filter.param_name}-${filter.value.toLowerCase()}`;
        const linkValue = filter.value.toLowerCase();
        
        const link = `
          <a href="javascript:void(0)"
             data-name="${filter.param_name}"
             data-value="${linkValue}"
             data-child="${filter.child_param_name}"
             class="tw-cta-activable ${state.isDisabledForced} ${activeClass}"
             id="${id}">
            <span class="facet-checkbox__text tw-pointer-events-none" aria-hidden="true">
              <span class="facet-checkbox__text-label">${filter.value}</span>
            </span>
            <span class="tw-visually-hidden">${filter.label}</span>
          </a>
        `;
        
        return `<li class="list-menu__item facets__item facets__${filter.param_name} ${state.hiddenClass}">${link}</li>`;
      }

      /**
       * Build checkbox facet HTML
       * @param {Object} filter - Filter data
       * @param {Object} state - Facet state
       * @returns {String} HTML template
       */
      buildCheckboxFacetHTML(filter, state) {
        const id = `${this.idPrefix}-${filter.param_name}-${filter.value.toLowerCase()}`;
        
        const input = `
          <input name="${filter.param_name}"
                 type="checkbox"
                 ${state.checkedAttr}
                 ${state.disabledAttr}
                 id="${id}"
                 class="[&&]:tw-hidden"
                 value="${filter.value}">
          <span class="facet-checkbox__text tw-pointer-events-none" aria-hidden="true">
            <span class="facet-checkbox__text-label">${filter.value}</span>
          </span>
          <span class="tw-visually-hidden">${filter.label}</span>
        `;
        
        return `
          <li class="list-menu__item facets__item facets__${filter.param_name} ${state.hiddenClass}">
            <label class="facets__label facet-checkbox tw-cta-activable ${state.disabledClass} ${state.activeClass}" for="${id}">
              ${input}
            </label>
          </li>
        `;
      }

      /**
       * Parse HTML string into DOM element
       * @param {String} html - HTML string
       * @returns {HTMLElement} Parsed list item element
       */
      parseHTML(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return doc.querySelector('li');
      }

      /**
       * Render regular filter (non-macro categories)
       */
      renderFilter() {
        const filterData = this.getFilterData();
        if (!filterData) return;

        const orderStrategy = this.filterOrderer.getOrderStrategy(filterData.param_name);
        const orderedValues = orderStrategy
          ? this.filterOrderer.reorderFilters(filterData.values, orderStrategy, 'value')
          : filterData.values;

        const listElement = this.querySelector(RoFacetsFilters.SELECTORS.filterList);
        if (!listElement) return;

        orderedValues.forEach((value) => {
          const facetElement = this.createFacet(value, false, true);
          listElement.appendChild(facetElement);
        });
      }

      /**
       * Get filter data from JSON script tag
       * @returns {Object|null} Filter data object or null
       */
      getFilterData() {
        const jsonElement = this.querySelector(RoFacetsFilters.SELECTORS.jsonData);
        if (!jsonElement?.textContent) return null;

        try {
          const filterJson = JSON.parse(jsonElement.textContent);
          this.filterData = this.filterData || filterJson;
          return this.filterData;
        } catch (error) {
          console.error('Error parsing filter JSON:', error);
          return null;
        }
      }
    }
  );
}


